import React from 'react';
import { LucideIcon } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
    size?: 'sm' | 'md' | 'lg' | 'icon';
    icon?: LucideIcon;
    loading?: boolean;
    fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
    children,
    variant = 'primary',
    size = 'md',
    icon: Icon,
    loading = false,
    fullWidth = false,
    className = '',
    disabled,
    ...props
}) => {
    const baseStyles = 'inline-flex items-center justify-center font-medium rounded-md transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
        primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-dark-900',
        secondary: 'bg-dark-600 text-dark-100 hover:bg-dark-500 focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 focus:ring-offset-dark-900',
        danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-dark-900',
        ghost: 'bg-transparent text-dark-300 hover:bg-dark-700 hover:text-dark-100'
    };

    const sizes = {
        icon: 'px-2 py-2 text-sm',
        sm: 'px-3 py-2 text-sm',
        md: 'px-4 py-2 text-sm',
        lg: 'px-6 py-3 text-base'
    };

    const width = fullWidth ? 'w-full' : '';

    return (
        <button
            className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${width} ${className}`}
            disabled={loading || disabled}
            {...props}
        >
            {loading ? (
                <>
                    <div className="mr-2 w-4 h-4 rounded-full border-2 border-current animate-spin border-t-transparent" />
                    Processing...
                </>
            ) : (
                <>
                    {Icon && <Icon className="mr-2 w-4 h-4" />}
                    {children}
                </>
            )}
        </button>
    );
};

export default Button;